/* eslint-disable import/order */
import React, { useContext } from 'react';
import { func } from 'prop-types';
import * as R from 'ramda';

// Assets
import { ReactComponent as Remove } from 'assets/img/remove.svg';
import { ReactComponent as MakePrimary } from 'assets/img/make_primary.svg';
import { ReactComponent as MakeLast } from 'assets/img/make_last.svg';

// Components
import { MDropdown } from 'common';
import { Box, Typography } from '@mui/material';
import ReorderRoundedIcon from '@mui/icons-material/ReorderRounded';
import Buttons from './components/Buttons';

// Last Wizard
import LastStep1 from './wizards/last/LastStep1.connector';
import LastStep2 from './wizards/last/LastStep2.connector';
import LastStep3 from './wizards/last/LastStep3.connector';
import LastStep5 from './wizards/last/LastStep5';
import LastStep6 from './wizards/last/LastStep6.connector';

// Primary Wizard
import PrimaryStep1 from './wizards/primary/PrimaryStep1.connector';
import PrimaryStep2 from './wizards/primary/PrimaryStep2.connector';
import PrimaryStep3 from './wizards/primary/PrimaryStep3.connector';
import PrimaryStep5 from './wizards/primary/PrimaryStep5';
import PrimaryStep6 from './wizards/primary/PrimaryStep6.connector';

// Remove Wizard
import RemoveStep1 from './wizards/remove/RemoveStep1';
import RemoveStep2 from './wizards/remove/RemoveStep2';
import RemoveStep3 from './wizards/remove/RemoveStep3';
import RemoveStep5 from './wizards/remove/RemoveStep5';
import RemoveStep6 from './wizards/remove/RemoveStep6.connector';

// Sequence Wizard
import SequenceStep1 from './wizards/sequence/SequenceStep1';
import SequenceStep2 from './wizards/sequence/SequenceStep2';
import SequenceStep3 from './wizards/sequence/SequenceStep3.connector';
import SequenceStep4 from './wizards/sequence/SequenceStep4';
import SequenceStep6 from './wizards/sequence/SequenceStep6';
import SequenceStep7 from './wizards/sequence/SequenceStep7.connector';

import FutureDate from './components/FutureDate.connector';

// context
import { SequencingWizardContext } from './SequencingWizardContext';

// Styles
import {
  iconWrapperStyles,
  intitalStepStyles,
  selectionButtonSelectedStyles,
  selectionButtonStyles,
  titleStyles,
} from './InitialStep.styles';
import { useTheme } from '@mui/material/styles';

const buttons = [
  {
    content: 'Make an Inventory Owner primary',
    icon: <MakePrimary />,
    id: 0,
    value: 'primary',
  },
  {
    content: 'Make an Inventory Owner last in multiple sequences',
    icon: <MakeLast />,
    id: 1,
    value: 'last',
  },
  {
    content: 'Apply a sequence of Inventory Owners',
    icon: <ReorderRoundedIcon sx={{ color: 'primary.dark', fontSize: 48 }} />,
    id: 2,
    value: 'sequence',
  },
  {
    content: 'Remove Inventory Owner(s) from all sequences',
    icon: <Remove />,
    id: 3,
    value: 'remove',
  },
];

const InitialStep = props => {
  // setting up children here before defining props
  // this is so we pass all required/global props
  // from context into each step, and they all
  // are there on load and prevents the need for
  // default prop types in each step
  const wizards = {
    last: [
      <InitialStep key="initial" {...props} />,
      <LastStep1 key="last1" {...props} />,
      <LastStep2 key="last2" {...props} />,
      <LastStep3 key="last3" {...props} />,
      <FutureDate key="last4" {...props} />,
      <LastStep5 key="last5" {...props} />,
      <LastStep6 key="last6" {...props} />,
    ],
    primary: [
      <InitialStep key="initial" {...props} />,
      <PrimaryStep1 key="primary1" {...props} />,
      <PrimaryStep2 key="primary2" {...props} />,
      <PrimaryStep3 key="primary3" {...props} />,
      <FutureDate key="primary4" {...props} />,
      <PrimaryStep5 key="primary5" {...props} />,
      <PrimaryStep6 key="primary6" {...props} />,
    ],
    remove: [
      <InitialStep key="initial" {...props} />,
      <RemoveStep1 key="remove1" {...props} />,
      <RemoveStep2 key="remove2" {...props} />,
      <RemoveStep3 key="remove3" {...props} />,
      <FutureDate key="remove4" {...props} />,
      <RemoveStep5 key="remove5" {...props} />,
      <RemoveStep6 key="remove6" {...props} />,
    ],
    sequence: [
      <InitialStep key="initial" {...props} />,
      <SequenceStep1 key="sequence1" {...props} />,
      <SequenceStep2 key="sequence2" {...props} />,
      <SequenceStep3 key="sequence3" {...props} />,
      <SequenceStep4 key="sequence4" {...props} />,
      <FutureDate key="sequence5" {...props} />,
      <SequenceStep6 key="sequence6" {...props} />,
      <SequenceStep7 key="sequence7" {...props} />,
    ],
  };

  const { close, next, setchildren } = props;

  const {
    affiliatesOptions,
    data: { currentAffiliate, flow },
    initialData,
    setData,
  } = useContext(SequencingWizardContext);
  const theme = useTheme();
  const handleRightButtonClick = () => {
    next();
    setData(prevData => ({ ...prevData, currentAffiliate, flow }));
    setchildren(wizards[flow]);
  };

  const handleAffiliateChange = ({ value: val }) => {
    // find matching affiliate then pass the whole object
    // so when fetching on step 1 of any wizard the fetch
    // has access to the dataCompanyId and value (idName)
    const foundAffiliate = affiliatesOptions.find(({ value }) => value === val);
    if (val !== currentAffiliate) setData({ ...initialData, currentAffiliate, flow });
    setData(prevData => ({ ...prevData, currentAffiliate: foundAffiliate }));
  };

  const handleSelection = val => {
    if (!R.isEmpty(flow) && val !== flow) setData({ ...initialData, currentAffiliate, flow });
    setData(prevData => ({ ...prevData, flow: val }));
  };

  return (
    <>
      <Box sx={intitalStepStyles(theme)}>
        {affiliatesOptions.length > 1 && (
          <MDropdown
            id="affiliate-select"
            items={affiliatesOptions}
            label="Affiliate"
            name="affiliate"
            onChange={handleAffiliateChange}
            value={currentAffiliate?.value}
          />
        )}
        <Typography sx={{ ...titleStyles(theme) }} variant="body1">
          Apply the following change to multiple locations and products
        </Typography>
        {buttons.map(({ icon, id, content, value }) => (
          <Box
            key={id}
            onClick={() => handleSelection(value)}
            sx={{
              ...selectionButtonStyles(theme),
              ...(value === flow ? selectionButtonSelectedStyles(theme) : ''),
              borderRadius: 0,
            }}
            type="button"
          >
            <Box sx={iconWrapperStyles(theme)}>{icon}</Box>
            {content}
          </Box>
        ))}
      </Box>
      {/* Render the buttons outside the Box so that the wizard scrolls */}
      <Buttons
        disabled={R.isEmpty(flow)}
        handleLeftButtonClick={close}
        handleRightButtonClick={handleRightButtonClick}
        leftLabel="Close"
        rightLabel="Continue"
      />
    </>
  );
};

InitialStep.propTypes = {
  close: func,
  next: func,
  setData: func,
  setchildren: func,
};

InitialStep.defaultProps = {
  close: () => {},
  next: () => {},
  setData: () => {},
  setchildren: () => {},
};

export default InitialStep;
